import { Component, OnInit, OnDestroy, ViewChild, ViewEncapsulation, NgZone, ChangeDetectorRef, inject, HostListener } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, filter, finalize } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { AlertController, IonContent, ModalController, NavController, Platform } from '@ionic/angular';
import { GlobalService } from "./services/global.service";
import { StorageService } from './services/storage.service';
import { UserService } from './services/user.service';
import { CalendarService } from './services/calendar.service';
import { WebSocketService } from './services/websocket.service';
import { SplashScreen } from '@capacitor/splash-screen';
import { initializeApp } from "firebase/app";
import { environment } from "src/environments/environment";
import { DatabaseService } from './services/database.service';
import { WatchComplicationService, ClubData } from './services/watch-complication.service';
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { Keyboard } from '@capacitor/keyboard';
import { GarminService } from './services/garmin.service';
 
interface ClubDetails {
  club_name: string;
  membership_new_members: boolean;
  membership_form_completed: boolean;
  user_id: number;
  jwttoken: string;
  multiClubs: string;
  colors?: {
    primary: string;
    status_bar: string;
  };
}

interface NotificationData {
  title?: string;
  body?: string;
  cmc_url?: string;
  url?: string;
  user_id?: string;
}

const APP_CONFIG = {
  SPLASH_DELAY: 1000,
  TAB_VALUES_DELAY: 2000,
  NAVIGATION_DELAY: 10,
  USAGE_STATS_DELAY: 1000,
  SCROLL_DURATION: 200
} as const;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild(IonContent, { static: false }) content!: IonContent;

  private readonly router = inject(Router);
  private readonly globalService = inject(GlobalService);
  private readonly storage = inject(StorageService);
  private readonly userService = inject(UserService);
  private readonly calendarService = inject(CalendarService);
  private readonly webSocketService = inject(WebSocketService);
  private readonly platform = inject(Platform);
  private readonly zone = inject(NgZone);
  private readonly alertController = inject(AlertController);
  private readonly navController = inject(NavController);
  private readonly modalController = inject(ModalController);
  private readonly garminService = inject(GarminService);
  private readonly databaseService = inject(DatabaseService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly watchService = inject(WatchComplicationService);

  public appState = {
    isClubAdmin: false,
    isAdmin: false,
    canTimeEvents: false,
    canScanEvents: Capacitor.isNativePlatform(),
    hasAssociations: false,
    isLoggedIn: false,
    isAuthenticated: false,
    hasMultiClubs: false,
    showTabs: true,
    darkMode: false,
    preferWheel: false
  };

  public userData = {
    token: '',
    cmc_url: '',
    club_id: '',
    jwttoken: '',
    user_id: ''
  };

  public uiState = {
    sideBarMenu: null as any[] | null,
    readySteady: null as string | null,
    when: false,
    pageTitle: '',
    showingMembershipForm: false,
    pushPermission: null,
    stravaConnected: false,
    garminConnected: false,
    deviceOS: '',
  };

  public readonly config = {
    appName: this.globalService.appName || '',
    apiURL: this.globalService.apiURL || '',
    stripeKey: environment.stripeKey || '',
    appVersion: "web",
    appType: "web",
    appPlatform: "web",
    isApp: true,
    isNative: Capacitor.isNativePlatform()
  };

  public theme = {
    primary_color: this.globalService.primary_color,
    secondary_color: this.globalService.primary_color,
    tertiary_color: this.globalService.primary_color,
    statusBarColor: '',
    statusBarText: ''
  };

  public notifications = {
    newAnnouncement: false,
    newMessageBoard: false,
    newDM: false,
    newGroupMsg: false,
    newOffer: false
  };

  private pendingNotificationUrl: string | null = null;

  public thisYear = new Date().getFullYear();
  public clubSubscribed: any;
  public isLoadingClubDetails = false;

  statusBarColor: string = environment.appColor;
  statusBarText: string = environment.statusBarText;

  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.uiState.deviceOS = '';
    this.uiState.pageTitle = '';

    this.initialiseApp();
    this.initialiseFirebase();
    this.setupWebPushListeners();
    this.setupAppListeners();
  }

  async ngOnInit(): Promise<void> {
    await this.globalService.checkAuth();

    this.config.isApp = this.globalService.isApp;

    this.setupSubscriptions();

    if (Capacitor.getPlatform() === 'ios') {
      await this.syncWatchCredentialsIfLoggedIn();
      this.setupAuthListener();
      this.setupClubChangeListener();
    }
  }

  private setupSubscriptions(): void {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe();

    combineLatest([
      this.userService.showMembershipForm,
      this.userService.sideBarMenu,
      this.userService.readySteady,
      this.userService.clubID$,
      this.userService.isAdmin,
      this.userService.canTimeEvents,
      this.userService.hasAssociations,
      this.userService.clubSubscription
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([
        showMembershipForm, sideBarMenu, readySteady, clubID,
        isAdmin, canTimeEvents, hasAssociations, clubSubscription
      ]) => {
        this.uiState.showingMembershipForm = showMembershipForm;
        this.uiState.sideBarMenu = sideBarMenu;
        this.uiState.readySteady = readySteady;
        this.userData.club_id = clubID != null ? clubID.toString() : '';
        this.appState.isAdmin = isAdmin;
        this.appState.canTimeEvents = canTimeEvents;
        this.appState.hasAssociations = hasAssociations;
        this.clubSubscribed = clubSubscription;
      });

    this.globalService.isAuthenticated
      .pipe(takeUntil(this.destroy$))
      .subscribe((isAuthenticated: boolean | null) => {
        this.appState.isAuthenticated = false;
        if (isAuthenticated) {
          this.handleSuccessfulAuthentication();
        }
      });

    this.globalService.isSwitchingClub
      .pipe(takeUntil(this.destroy$))
      .subscribe(isSwitching => {
        this.appState.showTabs = !isSwitching;
        this.cdr.detectChanges();
      });
  }

  private async handleSuccessfulAuthentication(): Promise<void> {
    this.appState.isAuthenticated = true;
    this.appState.isLoggedIn = true;
    this.appState.showTabs = true;

    // ── ANDROID / GARMIN — DO NOT REMOVE (see CLAUDE.md) ───────────────────
    // Android-only, but must live in this shared file — no other post-login
    // hook exists on Android. iOS Claude: leave all four lines below alone.
    //
    // ensureLocationPermission() is NOT an iOS permission call — it prevents a
    // native crash on Android 8–11 where startForegroundService() crashes if
    // ACCESS_FINE_LOCATION is not granted before pushCredentials() fires.
    await this.garminService.ensureLocationPermission().catch(() => {});
    await this.garminService.pushCredentials().catch(() => {});
    this.garminService.startListening().catch(() => {});
    this.garminService.resumePendingShare().catch(() => {});
    // ── END ANDROID / GARMIN ─────────────────────────────────────────────────

    const multiClub = await this.storage.getString('multiClubs');
    this.userService.multiClubMember.next(false);

    if (multiClub?.value == '1') {
      this.appState.hasMultiClubs = true;
      this.userService.multiClubMember.next(true);
    }

    const user = await this.userService.getUser();
    this.userData.user_id = user?.user_id?.toString() || '';

    setTimeout(() => {
      this.globalService.getTabValues();
    }, APP_CONFIG.TAB_VALUES_DELAY);

    if (this.pendingNotificationUrl) {
      const urlToNavigate = this.pendingNotificationUrl;
      this.pendingNotificationUrl = null;

      setTimeout(() => {
        this.zone.run(() => {
          this.navController.navigateRoot(urlToNavigate);
        });
      }, 500);
    }
  }

  private setupWebPushListeners(): void {
    this.globalService.checkWebPushPermissions();

    FirebaseMessaging.addListener("notificationReceived", (event) => {
      return this.handleNotificationReceived(event.notification.data as NotificationData);
    });

    FirebaseMessaging.addListener("notificationActionPerformed", async (event) => {
      await this.handleNotificationAction(event);
    });

    if (Capacitor.getPlatform() == "web") {
      this.setupServiceWorkerListener();
    }
  }

  private handleNotificationReceived(data: NotificationData): void {
    const { cmc_url: url, user_id: fromUser } = data;

    const currentRoom = this.webSocketService.getCurrentRoom();
    const isInActiveChat = this.isNotificationForActiveChat(url, currentRoom);

    if (url?.includes("group-chat")) {
      if (!isInActiveChat) this.userService.newGroupMsg.next(true);
    } else if (url?.includes("direct-message")) {
      if (!isInActiveChat) this.userService.newDM.next(fromUser || null);
    }

    this.globalService.usageStats('NOTIFICATION RECEIVED');
  }

  private async handleNotificationAction(event: any): Promise<void> {
    const notification = event?.notification;
    const notificationData = notification?.data || {};
    const notificationId = notification?.id;
    const rawUrl = notificationData['cmc_url'];
    const url = this.normalizeNotificationUrl(rawUrl);

    if (!environment.production) console.log('🔔 Notification action received:', {
      notificationId, rawUrl, url, notificationData,
      isAuthenticated: this.appState.isAuthenticated
    });

    if (notificationId) {
      try {
        await FirebaseMessaging.removeDeliveredNotifications({
          notifications: [{ id: notificationId }]
        });
      } catch (error) {
        console.warn('⚠️ Failed to remove delivered notification:', error);
      }
    }

    if (!url) {
      console.warn('⚠️ Notification action ignored due to invalid cmc_url:', rawUrl);
      return;
    }

    this.userData.cmc_url = url;
    this.updateNotificationStateFromUrl(url, notificationData['user_id']);

    setTimeout(() => {
      this.globalService.usageStats('NOTIFICATION CLICKED');
    }, APP_CONFIG.USAGE_STATS_DELAY);

    if (!this.appState.isAuthenticated) {
      if (!environment.production) console.log('🔔 App not authenticated, storing URL for deferred navigation:', url);
      this.pendingNotificationUrl = url;
      return;
    }

    if (!environment.production) console.log('🔔 Navigating to:', url);

    try {
      await this.globalService.postPageViewed(url);
    } catch (error) {
      console.warn('⚠️ postPageViewed failed for notification route:', url, error);
    }

    try {
      const navigated = await this.zone.run(() => this.navController.navigateRoot(url));
      if (!navigated) {
        console.warn('⚠️ Notification navigation not completed for route:', url);
      }
    } catch (error) {
      console.error('❌ Notification navigation failed for route:', url, error);
    }
  }

  private normalizeNotificationUrl(rawUrl: unknown): string | null {
    if (typeof rawUrl !== 'string') return null;

    const trimmedUrl = rawUrl.trim();
    if (!trimmedUrl) return null;

    const deepLinkPath = this.extractAppPathFromUrl(trimmedUrl);
    const normalizedPath = deepLinkPath || trimmedUrl;
    const withLeadingSlash = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;

    return withLeadingSlash.replace(/\/+/g, '/');
  }

  private setupServiceWorkerListener(): void {
    navigator.serviceWorker.addEventListener("message", (event: any) => {
      const notificationData = this.extractNotificationData(event.data);
      if (!notificationData) return;

      const { cmc_url: url, url: fallbackUrl, user_id: fromUser, title = 'Notification', body = 'You have a new notification' } = notificationData;
      const finalUrl = url || fallbackUrl;

      if (!finalUrl) {
        console.warn('No URL found in notification data:', notificationData);
        return;
      }

      this.userData.cmc_url = finalUrl;

      const notification = new Notification(title, { body });
      this.updateNotificationStateFromUrl(finalUrl, fromUser);

      notification.onclick = () => {
        this.globalService.postPageViewed(finalUrl).then(() => {
          this.globalService.usageStats('NOTIFICATION CLICKED');
        });
        this.navController.navigateRoot(finalUrl);
      };
    });
  }

  private extractNotificationData(data: any): NotificationData | null {
    if (data?.data?.data) return data.data.data;
    if (data?.data) return data.data;
    if (data) return data;
    return null;
  }

  private updateNotificationStateFromUrl(url: string, fromUser?: string): void {
    const currentRoom = this.webSocketService.getCurrentRoom();
    const isInActiveChat = this.isNotificationForActiveChat(url, currentRoom);

    if (url.includes("group-chat")) {
      if (!isInActiveChat) this.userService.newGroupMsg.next(true);
    } else if (url.includes("direct-message")) {
      if (!isInActiveChat) this.userService.newDM.next(fromUser || null);
    }
  }

  private isNotificationForActiveChat(notificationUrl: string | undefined, currentRoom: any): boolean {
    if (!notificationUrl || !currentRoom) return false;

    if (notificationUrl.includes('direct-message') && currentRoom.room_type === 'dm') {
      const dmMatch = notificationUrl.match(/direct-message\/(\d+)/);
      if (dmMatch && currentRoom.room_id === `dm_${dmMatch[1]}`) return true;
    }

    if (notificationUrl.includes('group-chat') && currentRoom.room_type === 'group') {
      const groupMatch = notificationUrl.match(/group-chat\/(\d+)/);
      if (groupMatch && currentRoom.room_id === `group_${groupMatch[1]}`) return true;
    }

    return false;
  }

  private setupAppListeners(): void {
    App.addListener('backButton', async ({ canGoBack }) => {
      const modalOpen = await this.modalController.getTop();
      if (modalOpen) {
        this.modalController.dismiss();
      } else if (!canGoBack) {
        App.exitApp();
      } else {
        this.navController.back();
      }
    });

    App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      if (!environment.production) console.log('🔗 Deep link received:', event.url);

      try {
        let url: URL | undefined;
        let oauthCode: string | null = null;

        try {
          url = new URL(event.url);
          oauthCode = url.searchParams.get('code');
        } catch {
          const codeMatch = event.url.match(/[?&]code=([^&]+)/);
          if (codeMatch) oauthCode = codeMatch[1];
        }

        if (oauthCode) {
          this.calendarService.pendingOAuthCode.next(oauthCode);

          await this.zone.run(async () => {
            await this.router.navigateByUrl('/user-settings');
          });

          await this.setDarkMode();
          setTimeout(() => {
            this.globalService.usageStats('Open URL - OAuth');
          }, APP_CONFIG.USAGE_STATS_DELAY);
          return;
        }
      } catch (error) {
        console.error('❌ Error parsing OAuth callback:', error);
      }

      const appPath = this.extractAppPathFromUrl(event.url);

      if (appPath) {
        await this.zone.run(async () => {
          await this.router.navigateByUrl(appPath);
        });
      } else {
        console.warn('⚠️ Unable to parse deep link URL:', event.url);
      }

      await this.setDarkMode();

      setTimeout(() => {
        this.globalService.usageStats('Open URL');
      }, APP_CONFIG.USAGE_STATS_DELAY);
    });

    // Single appStateChange handler covering Garmin, dark mode, and iOS watch sync
    App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        await this.setDarkMode();
        setTimeout(() => {
          this.globalService.usageStats('CHANGE STATE');
        }, APP_CONFIG.USAGE_STATS_DELAY);
        this.garminService.resumePendingShare().catch(() => {});

        if (Capacitor.getPlatform() === 'ios') {
          await this.syncWatchCredentialsIfLoggedIn();
        }
      }
    });

    Network.addListener('networkStatusChange', (status) => {
      if (!status.connected) {
        this.globalService.presentToast("Internet connection lost", 3000, "bottom");
      }
    });

    if (Capacitor.getPlatform() == 'android') {
      Keyboard.addListener('keyboardWillShow', () => {
        document.body.classList.add('keyboard_visible');
      });

      Keyboard.addListener('keyboardWillHide', () => {
        document.body.classList.remove('keyboard_visible');
      });
    }
  }

  private extractAppPathFromUrl(rawUrl: string): string | null {
    if (!rawUrl) return null;

    const knownHttpsBases = [
      'https://members.connectmyclub.co.uk',
      'https://mobile.connectmyclub.co.uk'
    ];

    const knownCustomSchemes = ['connectmyclub://', 'cmc://'];

    try {
      const parsedUrl = new URL(rawUrl);
      const host = parsedUrl.host.toLowerCase();

      if (host === 'members.connectmyclub.co.uk' || host === 'mobile.connectmyclub.co.uk') {
        const path = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
        return path.startsWith('/') ? path : `/${path}`;
      }

      if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
        return `/${parsedUrl.host}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
          .replace(/\/+/g, '/');
      }
    } catch {
      // fallthrough to string parsing
    }

    const normalizedUrl = rawUrl.trim();
    const lowerUrl = normalizedUrl.toLowerCase();

    for (const base of knownHttpsBases) {
      if (lowerUrl.startsWith(base)) {
        const path = normalizedUrl.substring(base.length) || '/';
        return path.startsWith('/') ? path : `/${path}`;
      }
    }

    for (const scheme of knownCustomSchemes) {
      if (lowerUrl.startsWith(scheme)) {
        const path = normalizedUrl.substring(scheme.length) || '/';
        const prefixedPath = path.startsWith('/') ? path : `/${path}`;
        return prefixedPath.replace(/\/+/g, '/');
      }
    }

    return null;
  }

  public async initialiseFirebase(): Promise<void> {
    initializeApp(environment.firebase);
  }

  async initialiseApp(): Promise<void> {
    this.platform.ready().then(async () => {
      setTimeout(async () => {
        await SplashScreen.hide();
        this.globalService.usageStats('START');
        await this.setDarkMode();
      }, APP_CONFIG.SPLASH_DELAY);

      if (!this.config.isNative) {
        await this.initialiseWebDatabase();
      }
    });
  }

  private async initialiseWebDatabase(): Promise<void> {
    await customElements.whenDefined('jeep-sqlite');
    const jeepSqliteEl = document.querySelector('jeep-sqlite');

    if (jeepSqliteEl) {
      await this.databaseService.initWebStore();
      await this.databaseService.initialiseDatabase();
    } else {
      console.warn('⚠️ jeep-sqlite element not found');
    }
  }

  async getNewClubDetails(clubID: string): Promise<void> {
    if (this.isLoadingClubDetails) return;

    this.isLoadingClubDetails = true;

    (await this.globalService.request('post', false, 'ionic_login', { 'new_club_id': clubID }))
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingClubDetails = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(async (res) => {
        if (!res) return;
        await this.clubSelection(clubID, res.clubsArr);
        await this.storage.setString('jwttoken', res.jwttoken);
      });
  }

  async clubSelection(clubID: string, clubsArr: ClubDetails[]): Promise<void> {
    this.userData.club_id = clubID;
    this.userService.clubID.next(Number(clubID));

    const clubDetails = clubsArr[Number(clubID)];
    const multiClubs = clubsArr.length > 1 ? "1" : "0";
    const clubDetailsWithMulti = { ...clubDetails, multiClubs };

    await this.storage.setString('clubDetails', JSON.stringify(clubDetailsWithMulti));
    await this.storage.setString('user_id', clubDetails.user_id.toString());
    await this.storage.setString('jwttoken', clubDetails.jwttoken);
    await this.storage.setString('multiClubs', multiClubs);

    this.globalService.updateClubName(clubDetails.club_name);

    this.userService.userDetails = {
      ...(this.userService.userDetails as any),
      user_id: clubDetails.user_id.toString(),
      club_id: clubID,
      membership_form_completed: clubDetails.membership_form_completed,
      jwttoken: clubDetails.jwttoken,
      multiClubs
    };

    this.globalService.isAuthenticated.next(true);
    this.globalService.checkWebPushPermissions();

    setTimeout(() => {
      this.router.navigate([this.userData.cmc_url], { replaceUrl: true });
    }, APP_CONFIG.NAVIGATION_DELAY);
  }

  async setDarkMode(): Promise<void> {
    const darkModeData = await this.storage.getString('dark');
    this.appState.darkMode = darkModeData?.value == 'true';

    if (this.appState.darkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    } 
  }

  scrollToTop(elementId: string): void {
    const element = document.getElementById(elementId);
    const scrollTop = element?.offsetTop || 0;
    this.content.scrollToPoint(0, scrollTop, APP_CONFIG.SCROLL_DURATION);
  }

  async logout(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Confirm logout',
      message: 'Are you sure you want to sign out?',
      cssClass: 'logout-alert',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Sign Out',
          cssClass: 'alert-button-confirm',
          handler: async () => {
            this.webSocketService.disconnect();
            await this.globalService.logout();
          }
        }
      ]
    });

    await alert.present();
  }

  async openClubPortal(): Promise<void> {
    (await this.globalService.request('get', false, 'ionic_club_portal_access', {}))
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.cdr.detectChanges())
      )
      .subscribe(async (res) => {
        if (!res) return;
        if (!res.url) {
          console.error('❌ No URL found in club portal response:', res);
          return;
        }
        window.open(res.url, '_blank');
      });
  }

  get isAuthenticated(): boolean { return this.appState.isAuthenticated; }
  get isNative(): boolean { return this.config.isNative; }
  get user_id(): string { return this.userData.user_id; }
  get isLoggedIn(): boolean { return this.appState.isLoggedIn; }
  get deviceOS(): string { return this.uiState.deviceOS || ''; }
  get appVersion(): string { return this.config.appVersion || 'web'; }
  get appType(): string { return this.config.appType || 'web'; }
  get appPlatform(): string { return this.config.appPlatform || 'web'; }
  get pageTitle(): string { return this.uiState.pageTitle || ''; }
  get canScanEvents(): boolean { return this.appState.canScanEvents; }
  get sideBarMenu(): string[] | null { return this.uiState.sideBarMenu; }

  @HostListener('click', ['$event'])
  handleLinkClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const anchorElement = target.closest('a') as HTMLAnchorElement;

    if (!anchorElement) return;

    const href = anchorElement.getAttribute('href') || anchorElement.href;
    if (!href) return;

    if (this.isInternalAppLink(href)) {
      event.preventDefault();
      event.stopPropagation();

      const appPath = this.extractAppPath(href);
      if (appPath) {
        this.navController.navigateForward(appPath);
      }
    }
  }

  private isInternalAppLink(href: string): boolean {
    if (!href) return false;
    const lowerHref = href.toLowerCase();
    return lowerHref.startsWith('/app/') ||
      lowerHref.includes('members.connectmyclub.co.uk/app/') ||
      lowerHref.includes('mobile.connectmyclub.co.uk/app/') ||
      lowerHref.includes('app.connectmyclub.co.uk/app/');
  }

  private extractAppPath(href: string): string | null {
    try {
      if (href.startsWith('/app/')) return href;

      const url = new URL(href);
      if (url.pathname.includes('/app/')) {
        return url.pathname + url.search + url.hash;
      }

      return null;
    } catch {
      const appMatch = href.match(/\/app\/[^\s"']*/);
      return appMatch ? appMatch[0] : null;
    }
  }

  private async syncWatchCredentialsIfLoggedIn(): Promise<void> {
    try {
      await this.watchService.syncExistingCredentials(() => {
        const userId = this.userService.userDetails?.user_id;
        const clubId = this.userService.clubID?.value;
        const token = this.userService.userDetails?.jwttoken;

        if (!userId || !token) return null;

        return { userId, clubId: clubId?.toString() || '0', token };
      });

      await this.syncAllClubsToWatch();
    } catch (error) {
      console.error('Error syncing watch credentials:', error);
    }
  }

  private async syncAllClubsToWatch(): Promise<void> {
    try {
      const clubDetailsArray = this.userService.clubDetailsSubscription?.value;
      const currentClubId = this.userService.clubID?.value;
      const userId = this.userService.userDetails?.user_id;
      const token = this.userService.userDetails?.jwttoken;

      if (!clubDetailsArray || clubDetailsArray.length === 0) return;

      const clubs: ClubData[] = clubDetailsArray.map(club => ({
        id: club.id,
        name: club.name,
        badgeURL: `${this.userService.config.siteUrl}club_logo/${club.id}`,
        isCurrentClub: club.id === currentClubId
      }));

      const currentClub = clubs.find(c => c.isCurrentClub) || clubs[0];

      // Fetch full payload (sessions, race, meet, QR, parkrun) so Bluetooth-only
      // watch users receive all content, not just club badges.
      const watchApiData = userId && token
        ? await this.watchService.fetchWatchApiData(userId, token)
        : null;

      await this.watchService.updateComplication({
        clubs,
        clubBadgeURL: currentClub.badgeURL,
        clubName: currentClub.name,
        isClubMember: true,
        userID: userId,
        ...(watchApiData ?? {})
      });
    } catch (error) {
      console.error('Error syncing data to watch:', error);
    }
  }

  private setupClubChangeListener(): void {
    this.userService.clubID$.pipe(takeUntil(this.destroy$)).subscribe(async (clubId) => {
      if (clubId) await this.syncAllClubsToWatch();
    });
  }

  private setupAuthListener(): void {
    this.globalService.isAuthenticated.pipe(takeUntil(this.destroy$)).subscribe(async (isAuth) => {
      const userId = this.userService.userDetails?.user_id;
      const clubId = this.userService.clubID?.value;
      const token = this.userService.userDetails?.jwttoken;

      if (isAuth) {
        if (userId && token) {
          await this.watchService.sendCredentials(userId, clubId?.toString() || '0', token);
        }
      } else {
        await this.watchService.sendCredentials('', '', '');
      }
    });
  }

  ngOnDestroy(): void {
    this.webSocketService.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
